import { cn } from '@/lib/utils';

/**
 * The form treatment from the portal design canvas.
 *
 * ── Why these are literals and not the shared Input/Label ───────────────────
 *
 * The shared components are shadcn's defaults - 36px controls, a 1px border,
 * a 6px radius, 14px text - and the design draws something measurably
 * different: 38px tall, a 1.5px border at 9px radius, 12.5px text, and an
 * 11.5px semibold label six pixels above it. Those numbers are the design,
 * not a preference, and a dialog built from the defaults reads as a generic
 * form sitting inside a designed application.
 *
 * They are kept here rather than pushed into `src/shared/ui` deliberately:
 * that directory is synced from `web/shared` and shared with the other
 * portals, so restyling Input there restyles every form in every portal.
 * This is the customers dialogs adopting the design's treatment on their own;
 * if the other portals follow, this is what moves.
 *
 * Fighting the shared component with `className` was the alternative and is
 * worse: `Input` carries `md:text-sm` and `SelectTrigger` carries
 * `data-[size=default]:h-9`, both of which are variants that a plain utility
 * cannot override, so the result silently reverts above the `md` breakpoint.
 *
 * Separate from fields.tsx only because a file that exports both components
 * and constants loses fast refresh.
 */

/** 11.5px semibold, #48536A, 6px above its control. */
export const FIELD_LABEL =
  'mb-1.5 block text-[11.5px] font-semibold text-neutral-700 dark:text-neutral-300';

/** 38px tall, 1.5px border at 9px radius, 12.5px text. */
export const FIELD_CONTROL =
  'h-9.5 w-full rounded-[9px] border-[1.5px] border-input bg-background px-3 text-[12.5px] text-foreground transition-[color,box-shadow] outline-none placeholder:text-neutral-400 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40';

/**
 * Email, phone and tax numbers, in the mono face and a half-point smaller.
 *
 * Not decoration: these are the three fields somebody reads back character by
 * character to a customer on the phone, and Plex Mono is the face that keeps
 * 0/O and 1/l/I apart. The design marks exactly these three.
 */
export const FIELD_MONO = 'font-mono text-[12px]';

export const FIELD_HINT = 'mt-1.75 text-[10.5px] text-neutral-400';
export const FIELD_ERROR = 'mt-1.5 text-[11.5px] text-destructive';

/** The footer pair: 38px, 9px radius, 12.5px semibold. */
const DIALOG_BUTTON =
  'inline-flex h-9.5 items-center justify-center rounded-[9px] px-4.5 text-[12.5px] font-semibold whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-60';

export const DIALOG_BUTTON_PRIMARY = cn(
  DIALOG_BUTTON,
  'bg-primary text-primary-foreground hover:bg-brand-500',
);

export const DIALOG_BUTTON_OUTLINE = cn(
  DIALOG_BUTTON,
  'border border-input bg-background text-foreground hover:border-primary',
);

/** The design's close affordance: a 30px bordered square, not a bare glyph. */
export const DIALOG_CLOSE =
  'flex size-7.5 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50';
